/**
 * PublicLayout Component
 *
 * Main layout wrapper for public pages with header, footer, and navigation.
 */

import React from 'react';
import { Header } from '../Header/Header';
import { Footer } from '../Footer/Footer';
import { MobileBottomNav } from '../Navigation/MobileBottomNav';
import { AnnouncementBanner } from '../AnnouncementBanner/AnnouncementBanner';
import { ScrollToTop } from './ScrollToTop';
import { useAuth } from '../../../contexts/AuthContext';

interface PublicLayoutProps {
  children: React.ReactNode;
  transparentHeader?: boolean;
  hideFooter?: boolean;
  hideBottomNav?: boolean;
  unreadMessages?: number;
  savedCount?: number;
}

export const PublicLayout: React.FC<PublicLayoutProps> = ({
  children,
  transparentHeader = false,
  hideFooter = false,
  hideBottomNav = false,
  unreadMessages = 0,
  savedCount = 0,
}) => {
  const { user, isAuthenticated, logout } = useAuth();
  return (
    <div className="min-h-screen flex flex-col">
      {/* Site-wide announcement banner */}
      <AnnouncementBanner />

      {/* Header */}
      <Header
        transparent={transparentHeader}
        user={user ? { displayName: user.displayName, photoURL: user.photoURL, role: user.role } : null}
        isAuthenticated={isAuthenticated}
        unreadMessages={unreadMessages}
        onLogout={logout}
      />

      {/* Main Content */}
      <main
        className={`flex-1 ${
          transparentHeader ? '' : 'pt-16 lg:pt-20'
        } ${hideBottomNav ? '' : 'pb-16 md:pb-0'}`}
      >
        {children}
      </main>

      {/* Footer */}
      {!hideFooter && <Footer />}

      {/* Mobile Bottom Navigation */}
      {!hideBottomNav && (
        <MobileBottomNav
          unreadMessages={unreadMessages}
          savedCount={savedCount}
          isAuthenticated={!!user}
        />
      )}

      {/* Scroll to Top Button */}
      <ScrollToTop />
    </div>
  );
};

export default PublicLayout;
